import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FormRule } from 'antd';
import { Checkbox, Form, Input, Select, InputNumber } from 'antd';
import { MAX_INT_32 } from 'shared/constants/validation';
import { withRoleGuard } from 'shared/hocs/with-role-guard';
import { UserRole } from 'types/user';
import type { IUserFormProps, IUserFormFields } from 'components/users/user-form/types';

const validationRule: Record<keyof IUserFormFields, FormRule[]> = {
  email: [
    { required: true, message: 'Email is required' },
    { type: 'email', message: 'Invalid email' },
  ],
  name: [{ required: true, message: 'Name is required' }],
  role: [{ required: true, message: 'Role is required' }],
  isBlocked: [{ required: false }],
  phone: [{ required: false }, { max: 20 }],
  phoneDescription: [{ required: false }, { max: 50 }],
  bonusLimit: [{ required: false, type: 'integer', min: 0, max: MAX_INT_32 }],
  bonusPercent: [{ required: false, type: 'number', min: 0, max: 100 }],
};

export const UserFormComponent: React.FC<IUserFormProps> = (props) => {
  const { form, formId, onFinish, initialValues, showBlockedCheckbox } = props;
  const { t } = useTranslation();

  const roleOptions = Object.values(UserRole).map((value) => ({
    label: t(`user:${value}`),
    value,
    disabled: value === UserRole.admin,
  }));

  const isSelectDisabled = initialValues?.role === UserRole.admin;

  return (
    <Form form={form} id={formId} layout='vertical' initialValues={initialValues} onFinish={onFinish}>
      <Form.Item name='email' label={t('common:email')} rules={validationRule.email}>
        <Input placeholder={t('common:email')} />
      </Form.Item>
      <Form.Item name='name' label={t('common:name')} rules={validationRule.name}>
        <Input placeholder={t('common:name')} />
      </Form.Item>
      <Form.Item name='role' label={t('common:role')} rules={validationRule.role}>
        <Select disabled={isSelectDisabled} options={roleOptions} placeholder={t('common:role')} />
      </Form.Item>
      <Form.Item name='phone' label={t('common:phone')} rules={validationRule.phone}>
        <Input placeholder={t('common:phone')} />
      </Form.Item>
      <Form.Item name='phoneDescription' label={t('common:phone_description')} rules={validationRule.phoneDescription}>
        <Input placeholder={t('common:phone_description')} />
      </Form.Item>
      <Form.Item name='bonusLimit' label={t('user:profit_limit')} rules={validationRule.bonusLimit}>
        <InputNumber placeholder={t('user:profit_limit')} addonAfter='€' />
      </Form.Item>
      <Form.Item name='bonusPercent' label={t('user:bonus_percent')} rules={validationRule.bonusPercent}>
        <InputNumber placeholder={t('user:bonus_percent')} addonAfter='%' />
      </Form.Item>

      {showBlockedCheckbox && (
        <Form.Item name='isBlocked' valuePropName='checked' rules={validationRule.isBlocked}>
          <Checkbox disabled={isSelectDisabled}>{t('user:block_user')}</Checkbox>
        </Form.Item>
      )}
    </Form>
  );
};

export const UserForm = withRoleGuard<IUserFormProps>(UserFormComponent, [UserRole.admin]);
